import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputAdornment, MenuItem } from '@mui/material';
import { useTheme } from 'styled-components';
import { Select } from '../../../../app/form/select/select';
import { CompoundingFrequency } from '../../../../../utils/compound-interest/types';
import { COMPOUNDING_OPTIONS } from '../../../../../utils/compound-interest/constants';
import { getOpacity } from '../../../../../theme/utils/get-rgb';
import { useCompoundInterestCalculatorContext } from '../../calculator-context/compound-interest-calculator-context';
import { CompoundInterestDepositFrequencyAdornment } from '../deposit-frequency-adornment/compound-interest-deposit-frequency-adornment';
import { TextFieldWithSlider } from '../../../../app/form/textfield-with-slider/textfield-with-slider';
import { CompoundInterestInputSuffix } from '../input-suffix/compound-interest-input-suffix';

type EditableNumberField = 'initialDeposit' | 'regularDeposit' | 'interestRate' | 'duration' | 'inflationRate';

type SliderWrapperStyle = React.CSSProperties & Record<string, string>;

export const CompoundInterestForm: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { params, sliderRanges, initialDepositSliderMax, regularDepositSliderMax, handleChange, handleNumberChange } =
        useCompoundInterestCalculatorContext();

    const [draftValues, setDraftValues] = useState<Partial<Record<EditableNumberField, string>>>({});
    const [sliderDraftValues, setSliderDraftValues] = useState<Partial<Record<EditableNumberField, number>>>({});

    const fieldValues = useMemo(
        () => ({
            initialDeposit: params.initialDeposit,
            regularDeposit: params.regularDeposit,
            interestRate: params.interestRate,
            duration: params.duration,
            inflationRate: params.inflationRate,
        }),
        [params.duration, params.inflationRate, params.initialDeposit, params.interestRate, params.regularDeposit],
    );

    const getDisplayValue = (field: EditableNumberField) => {
        const draftValue = draftValues[field];
        if (draftValue !== undefined) {
            return draftValue;
        }

        const sliderDraftValue = sliderDraftValues[field];
        if (sliderDraftValue !== undefined) {
            return String(sliderDraftValue);
        }

        return String(fieldValues[field]);
    };

    const getSliderValue = (field: EditableNumberField) => {
        if (sliderDraftValues[field] !== undefined) {
            return sliderDraftValues[field] as number;
        }

        return fieldValues[field];
    };

    const handleEditableNumberChange = (field: EditableNumberField, rawValue: string) => {
        setSliderDraftValues((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });

        setDraftValues((prev) => ({
            ...prev,
            [field]: rawValue,
        }));

        if (rawValue === '') {
            return;
        }

        const parsed = parseFloat(rawValue);
        if (Number.isNaN(parsed)) {
            return;
        }

        handleNumberChange(field, rawValue);
    };

    const handleSliderChange = (field: EditableNumberField, sliderValue: number) => {
        setSliderDraftValues((prev) => ({
            ...prev,
            [field]: sliderValue,
        }));
    };

    const handleSliderChangeCommitted = (field: EditableNumberField, sliderValue: number) => {
        setSliderDraftValues((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });

        handleChange(field, sliderValue);
    };

    const handleEditableNumberBlur = (field: EditableNumberField) => {
        const rawValue = draftValues[field];

        setDraftValues((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });

        if (rawValue === undefined || rawValue === '') {
            return;
        }

        const parsed = parseFloat(rawValue);
        if (Number.isNaN(parsed)) {
            return;
        }

        handleNumberChange(field, rawValue);
    };

    const sliderWrapperStyle: SliderWrapperStyle = {
        ['--slider-primary-main' as string]: theme.palette.color.primary.main,
        ['--slider-primary-light' as string]: theme.palette.color.primary.light,
        ['--slider-primary-main-20' as string]: getOpacity(theme.palette.color.primary.main, 0.12),
        ['--slider-primary-main-30' as string]: getOpacity(theme.palette.color.primary.main, 0.2),
        ['--slider-body-bg' as string]: theme.palette.background.body,
        ['--slider-common-border' as string]: theme.palette.common.border,
    };

    const inputSuffixStyle = {
        background: theme.select.base.open.background,
    };

    const numberFields = [
        {
            field: 'initialDeposit' as const,
            label: t('pages.compoundInterest.calculator.initialDeposit'),
            min: sliderRanges.initialDeposit.min,
            max: initialDepositSliderMax,
            step: sliderRanges.initialDeposit.step,
        },
        {
            field: 'regularDeposit' as const,
            label: t('pages.compoundInterest.calculator.regularDeposit'),
            min: sliderRanges.regularDeposit.min,
            max: regularDepositSliderMax,
            step: sliderRanges.regularDeposit.step,
            endAdornment: (
                <InputAdornment position="end">
                    <CompoundInterestDepositFrequencyAdornment depositFrequency={params.depositFrequency} />
                </InputAdornment>
            ),
        },
        {
            field: 'interestRate' as const,
            label: t('pages.compoundInterest.calculator.interestRate'),
            min: sliderRanges.interestRate.min,
            max: sliderRanges.interestRate.max,
            step: sliderRanges.interestRate.step,
            endAdornment: <CompoundInterestInputSuffix style={inputSuffixStyle}>%</CompoundInterestInputSuffix>,
        },
        {
            field: 'duration' as const,
            label: t('pages.compoundInterest.calculator.duration'),
            min: sliderRanges.duration.min,
            max: sliderRanges.duration.max,
            step: sliderRanges.duration.step,
            endAdornment: (
                <CompoundInterestInputSuffix style={inputSuffixStyle}>
                    {t('pages.compoundInterest.calculator.years')}
                </CompoundInterestInputSuffix>
            ),
        },
        {
            field: 'inflationRate' as const,
            label: t('pages.compoundInterest.calculator.inflationRate'),
            min: sliderRanges.inflationRate.min,
            max: sliderRanges.inflationRate.max,
            step: sliderRanges.inflationRate.step,
            endAdornment: <CompoundInterestInputSuffix style={inputSuffixStyle}>%</CompoundInterestInputSuffix>,
        },
    ];

    const renderField = (item: (typeof numberFields)[number]) => (
        <TextFieldWithSlider
            key={item.field}
            sliderValue={getSliderValue(item.field)}
            sliderWrapperClassName="px-[2.5rem] [&_.MuiSlider-root]:h-[6px] [&_.MuiSlider-root]:text-[var(--slider-primary-main)] [&_.MuiSlider-thumb]:h-[18px] [&_.MuiSlider-thumb]:w-[18px] [&_.MuiSlider-thumb]:border-[2px] [&_.MuiSlider-thumb]:border-[var(--slider-body-bg)] [&_.MuiSlider-thumb]:bg-[var(--slider-primary-main)] [&_.MuiSlider-thumb]:shadow-[0_2px_4px_rgba(0,0,0,0.2)] [&_.MuiSlider-thumb:hover]:shadow-[0_0_0_8px_var(--slider-primary-main-20)] [&_.MuiSlider-thumb.Mui-focusVisible]:shadow-[0_0_0_8px_var(--slider-primary-main-20)] [&_.MuiSlider-thumb.Mui-active]:shadow-[0_0_0_12px_var(--slider-primary-main-30)] [&_.MuiSlider-track]:border-none [&_.MuiSlider-track]:bg-[linear-gradient(90deg,var(--slider-primary-main),var(--slider-primary-light))] [&_.MuiSlider-rail]:bg-[var(--slider-common-border)] [&_.MuiSlider-rail]:opacity-100"
            sliderWrapperStyle={sliderWrapperStyle}
            withDefaultSliderStyles={false}
            textFieldProps={{
                label: item.label,
                type: 'number',
                value: getDisplayValue(item.field),
                onChange: (event) => handleEditableNumberChange(item.field, event.target.value),
                onBlur: () => handleEditableNumberBlur(item.field),
                InputProps: item.endAdornment ? { endAdornment: item.endAdornment } : undefined,
                fullWidth: true,
            }}
            sliderProps={{
                min: item.min,
                max: item.max,
                step: item.step,
                onChange: (_, value) => handleSliderChange(item.field, value as number),
                onChangeCommitted: (_, value) => handleSliderChangeCommitted(item.field, value as number),
            }}
        />
    );

    return (
        <div className="grid gap-[4.8rem] lg:grid-cols-2 lg:gap-[6rem] vl:gap-[8rem]">
            <section className="min-w-0">
                <h2 className="mb-[3.2rem] text-[2.4rem] font-bold text-brand-secondary leading-[1.3]">
                    {t('pages.compoundInterest.calculator.investmentGroup')}
                </h2>
                <div className="space-y-[2rem]">
                    {[numberFields[0], numberFields[1], numberFields[3]].map(renderField)}
                </div>
            </section>
            <section className="min-w-0">
                <h2 className="mb-[3.2rem] text-[2.4rem] font-bold text-brand-secondary leading-[1.3]">
                    {t('pages.compoundInterest.calculator.growthGroup')}
                </h2>
                <div className="space-y-[2rem]">
                    {[numberFields[2], numberFields[4]].map(renderField)}
                    <div className="flex flex-col gap-[0.5rem] [&_.MuiInputAdornment-root]:w-auto [&_.MuiInputAdornment-root]:min-w-0 [&_.MuiInputAdornment-root]:max-w-none">
                        <Select
                            label={t('pages.compoundInterest.calculator.compoundingFrequency')}
                            value={params.compoundingFrequency}
                            onChange={(event) =>
                                handleChange(
                                    'compoundingFrequency',
                                    (event as unknown as React.ChangeEvent<{ value: unknown }>)?.target
                                        .value as CompoundingFrequency,
                                )
                            }
                            renderValue={() => {
                                const option = COMPOUNDING_OPTIONS.find(
                                    (item) => item.value === params.compoundingFrequency,
                                );
                                return option ? t(option.label) : '';
                            }}
                            fullWidth
                        >
                            {COMPOUNDING_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.label)}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                </div>
            </section>
        </div>
    );
};
